import type { TickStatus } from '@/types'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { statusConfig } from './utils'

interface TickProcessingIndicatorProps {
  /** Current tick status */
  status: TickStatus
  /** When the last tick completed */
  lastProcessedAt?: Date | null
  /** Show tooltip with details */
  showTooltip?: boolean
  /** Compact mode (icon only) */
  compact?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Visual indicator for tick processing status.
 *
 * Shows an icon and optional label with appropriate styling for each status.
 */
export function TickProcessingIndicator({
  status,
  lastProcessedAt,
  showTooltip = true,
  compact = false,
  className,
}: TickProcessingIndicatorProps) {
  const config = statusConfig[status]
  const Icon = config.icon

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  }

  const tooltipContent = (
    <div className="text-sm">
      <p>{config.tooltip}</p>
      {lastProcessedAt && status === 'completed' && (
        <p className="text-xs text-muted-foreground mt-1">
          at {formatTime(lastProcessedAt)}
        </p>
      )}
    </div>
  )

  const indicator = (
    <div
      className={cn(
        'flex items-center gap-1.5',
        !compact && 'px-2 py-1 rounded-md border',
        !compact && config.containerClass,
        compact && status === 'processing' && 'animate-pulse',
        className
      )}
      aria-label={`Tick status: ${config.label}`}
    >
      <Icon className={cn('h-4 w-4', config.iconClass)} />
      {!compact && (
        <span className="text-xs font-medium">{config.label}</span>
      )}
    </div>
  )

  if (!showTooltip) {
    return indicator
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{indicator}</div>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltipContent}</TooltipContent>
    </Tooltip>
  )
}
